import axios from "axios";
import { API_BASE_URL } from "../../../../js/api.js";
import {
  normalizarDocumento,
  formatarDocumento,
  extrairDocumentoDaBusca,
  normalizarTextoBusca,
} from "../utils/documento.js";
import {
  encontrarEmpresaCorreta,
  obterIdEmpresa,
  obterNomeEmpresa,
  obterDocumentoEmpresa,
  obterIdEmpresa as obterId,
  normalizarListaEmpresas,
} from "../utils/empresa.js";

import { obterTipoProgramaUsuarioLogado } from "../utils/tipoPrograma.js";

/**
 * Camada de serviço: única responsável por conversar com a API.
 * Nenhuma função aqui deve tocar o DOM (princípio herdado do CadastroEmpresa).
 */

function obterEmpresaBaseUrl() {
  const tipo = obterTipoProgramaUsuarioLogado();
  if (tipo === 0) return `${API_BASE_URL}/tce`;
  if (tipo === 1) return `${API_BASE_URL}/tca`;
  return `${API_BASE_URL}/tca-empregador`;
}

function criarHeadersAuth(token) {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

async function tentarFetchUrl(url, digitos, headers) {
  const res = await fetch(url, { method: "GET", headers });

  if (!res.ok) {
    throw new Error(`Erro HTTP ${res.status}`);
  }

  const dados = await res.json();
  const encontrada = encontrarEmpresaCorreta(dados, digitos);
  if (encontrada) return encontrada;

  if (
    dados &&
    typeof dados === "object" &&
    !Array.isArray(dados) &&
    obterId(dados)
  ) {
    return dados;
  }

  throw new Error("Empresa não correspondente");
}

async function buscarEmpresaPorDocumentoDireto(digitos, token) {
  const documentoFormatado = formatarDocumento(digitos);
  const headers = criarHeadersAuth(token);
  const baseUrl = obterEmpresaBaseUrl();
  const tipoProg = obterTipoProgramaUsuarioLogado();

  const tentativasUrl = [];
  for (const valor of [documentoFormatado, digitos]) {
    const encoded = encodeURIComponent(valor);
    tentativasUrl.push(`${baseUrl}?cnpj=${encoded}`);
    tentativasUrl.push(`${baseUrl}?documento=${encoded}`);
    tentativasUrl.push(`${baseUrl}?cpf=${encoded}`);
  }

  if (tipoProg === 0) {
    tentativasUrl.push(`${API_BASE_URL}/tce/dados-empresa`);
  } else {
    tentativasUrl.push(`${API_BASE_URL}/tca/dados-empresa`);
  }

  const urlsUnicas = Array.from(new Set(tentativasUrl));
  const promessas = urlsUnicas.map((url) => tentarFetchUrl(url, digitos, headers));
  return await Promise.any(promessas);
}

async function listarEmpresas(token, params = {}) {
  const baseUrl = obterEmpresaBaseUrl();
  const resposta = await axios.get(baseUrl, {
    params,
    headers: { Authorization: `Bearer ${token}` },
  });
  return normalizarListaEmpresas(resposta.data);
}

function encontrarEmpresaPorTermo(empresas, termoBusca) {
  const termo = String(termoBusca || "").trim();
  if (!termo) return null;

  const docDoTermo =
    (termo.match(/\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}|\d{11}/) || [])[0] || "";
  const docDigitosTermo = docDoTermo.replace(/\D/g, "");
  const nomeTermo = termo.replace(docDoTermo, "").replace(/\s+-\s+$/, "").trim();

  const nomeTermoNorm = normalizarTextoBusca(nomeTermo || termo);

  return (
    empresas.find((empresa) => {
      const nome = obterNomeEmpresa(empresa);
      const documento = obterDocumentoEmpresa(empresa);
      const combinado = normalizarTextoBusca(
        `${nome} ${documento} ${documento.replace(/\D/g, "")}`,
      );

      const matchNome = nomeTermoNorm ? combinado.includes(nomeTermoNorm) : true;
      const matchDoc = docDigitosTermo
        ? documento.replace(/\D/g, "") === docDigitosTermo
        : true;

      return matchNome && matchDoc;
    }) || null
  );
}

function paraResumo(empresa) {
  if (!empresa) return null;
  const tipoProg = obterTipoProgramaUsuarioLogado();
  return {
    id: obterIdEmpresa(empresa),
    nome: obterNomeEmpresa(empresa),
    documento: obterDocumentoEmpresa(empresa),
    tipo_programa: tipoProg === 0 ? 0 : (tipoProg === 2 ? 2 : (empresa?.tipo_programa ?? 1)),
    _origem: tipoProg === 0 ? "tce" : (tipoProg === 2 ? "tca-empregador" : (empresa?._origem || "tca")),
    raw: empresa,
  };
}

/**
 * Resolve uma empresa a partir de um termo livre (nome, CNPJ/CPF, ou ambos).
 * Tenta busca direta por documento quando aplicável e cai para listagem geral.
 */
export async function buscarEmpresaPorNomeCnpj(termoBusca, token) {
  const termoLimpo = extrairDocumentoDaBusca(termoBusca);
  const digitos = normalizarDocumento(termoLimpo);

  if (digitos.length === 11 || digitos.length === 14) {
    try {
      const encontrada = await buscarEmpresaPorDocumentoDireto(digitos, token);
      const resumo = paraResumo(encontrada);
      if (resumo?.id) return resumo;
    } catch (err) {
      console.warn(
        "Busca direta por documento falhou, tentando listagem geral:",
        err,
      );
    }
  }

  const empresas = await listarEmpresas(token);
  const encontrada = encontrarEmpresaPorTermo(empresas, termoBusca);
  if (!encontrada) {
    throw new Error("Empresa não encontrada por nome + CNPJ.");
  }

  return paraResumo(encontrada);
}

export async function listarEmpresasPaginadas({
  token,
  page = 1,
  limit = 50,
  busca = "",
  empresaId = null,
} = {}) {
  if (!token) throw new Error("Token obrigatório");

  const params = { page, limit };
  if (busca) params.busca = busca;
  if (empresaId) params.empresa_id = empresaId;

  return await listarEmpresas(token, params);
}

export async function listarEmpresasSimples(token) {
  if (!token) throw new Error("Token obrigatório");
  return await listarEmpresas(token);
}

export async function buscarEmpresaPorId(empresaId, token) {
  if (!empresaId) throw new Error("ID da empresa obrigatório");
  if (!token) throw new Error("Token obrigatório");

  const tipoProg = obterTipoProgramaUsuarioLogado();
  const url =
    tipoProg === 0
      ? `${API_BASE_URL}/tce/dados-empresa?empresaId=${encodeURIComponent(String(empresaId))}`
      : `${API_BASE_URL}/tca/dados-empresa?empresaId=${encodeURIComponent(String(empresaId))}`;
  try {
    const resposta = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return resposta.data;
  } catch {
    const baseUrl = obterEmpresaBaseUrl();
    const fallback = await axios.get(
      `${baseUrl}?empresa_id=${encodeURIComponent(String(empresaId))}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return fallback.data;
  }
}
